import type { Store } from './store';

import { EditorWindowState } from '../components/editorWindow/editorWindowState';
import { BookmarkWindowState } from '../components/bookmarks/bookmarkWindowState';
import { BookmarkListWindowState } from '../components/bookmarks/bookmarkListWindowState';
import { DeleteBookmarkListWindowState } from '../components/bookmarks/deleteBookmarkListWindowState';
import { ShareVideoWindowState, ShareMeetingAssetsWindowState } from '../components/jobs';
import { MultipleClipEditWindowState } from '../components/momentWindow/multipleClipEditWindowState';
import { TeamMembersWindowState } from '../components/teams';
import { TeamDictionaryWindowState } from '../components/teams/teamDictionaryWindowState';
import {
  CopyJobWindowState,
  AddJobToTeamWindowState,
  RemoveJobFromTeamWindowState,
  DeleteJobWindowState,
  RetryJobWindowState,
  IngestFromExternalLibraryWindowState,
  RemoveJobFromCliprWindowState,
  UploadThumbnailWindowState,
  JobViewStatusWindowState,
  VideoDetailsWindowState,
} from '../components/jobs';
import { ChangeJobOwnerWindowState } from '../components/jobs/changeJobOwnerWindowState';
import { RenameJobWindowState } from '../components/jobs/renameJobWindowState';
import { VideoInformationWindowState } from '../components/jobs/videoInformationWindowState';
import { DownloadJobWindowState } from '../components/jobs/downloadJobWindowState';
import { ExportToEdlWindowState } from '../components/jobs/exportToEdlWindowState';
import { DeleteStreamWindowState } from '../components/jobs/deleteStreamWindowState';
import { LiveFeedWindowState } from '../components/jobs/liveFeedWindowState';
import { ScheduleStreamWindowState } from '../components/jobs/scheduleStreamWindowState';
import { StreamAssetsWindowState } from '../components/jobs/streamAssetsWindowState';
import { StreamQueueSettingsWindowState } from '../components/jobs/streamQueueSettingsWindowState';
import { ManualIngestWindowState } from '../components/jobs/manualIngestWindowState';
import { LiveNotStreamingWindowState } from '../components/jobs/liveNotStreamingWindowState';
import { PlayerTutorialWindowState } from '../components/playerTutorial/playerTutorialWindowState';
import { PlayerTutorialConfirmationWindowState } from '../components/playerTutorial/playerTutorialConfirmationWindowState';
import { PlayerTutorialTopicsWindowState } from '../components/playerTutorial/playerTutorialTopicsWindowState';
import { PlayerTutorialHelpButtonTooltipState } from '../components/playerTutorial/playerTutorialHelpButtonState';
import { PostRegistrationTutorialState } from '../pages/auth/register/postRegistrationTutorialState';
import { SpeakerWindowState } from '../components/speakerWindow/speakerWindowState';
import { TrackWindowState } from '../components/trackWindow/trackWindowState';
import { DeleteTrackState } from '../pages/trainerVideoPage/deleteTrackState';
import { MergeTracksWindowState } from '../components/trackWindow/mergeTracksWindowState';
import { RemoveAccessPopupState } from '../components/teams/removeAccessPopupState';
import { LeaveTeamWindowState } from '../components/teams/leaveTeamWindowState';
import { DeleteTeamWindowState } from '../components/teams/deleteTeamWindowState';
import { BulkInvitesWindowState } from '../components/teams/bulkInvitesWindowState';
import { TeamProductWindowState } from '../components/teams/teamProductWindowState';
import { MakeTeamPublicWindowState } from '../components/teamSettings';
import { ToggleCliprGptWindowState } from '../components/teamSettings/toggleCliprGptWindowState';
import { TogglePublicSafetyWindowState } from '../components/teamSettings/togglePublicSafetyWindowState';
import { ToggleDeceptioAiWindowState } from '../components/teamSettings/toggleDeceptionAiWindowState';
import { ExternalLibraryUploadWindowState } from '../pages/uploadPage/externalLibraryUploadWindowState';
import { CancelUploadWindowState } from '../pages/uploadPage/cancelUploadWindowState';
import { DeleteMomentsPopupState } from '../components/momentWindow/deleteMomentsPopupState';
import { ClipWindowState } from '../components/momentWindow/clipWindowState';
import { CloseWindowConfirmationModalState } from '../components/momentWindow/closeWindowConfirmationModalState';
import { ConfirmationModalState } from '../pages/trainerVideoPage/confirmationModalState';
import { JobErrorsWindowState } from '../components/momentWindow/jobErrorsWindowState';
import { CommentDeleteWindowState } from '../components/playerComments/commentDeleteWindowState';
import { CredentialsExpiredWindowState } from '../pages/userDashboardPage/credentialsExpiredWindowState';
import { ZoomDateSelectorWindowState } from '../pages/userDashboardPage/zoomDateSelectorWindowState';
import { TrainerKeyboardShortcutsWindowState } from '../pages/trainerVideoPage/trainerKeyboardShortcutsWindowState';
import { InfoModalState } from '../components/infoModal';
import { DownloadWindowState } from '../components/download/downloadWindowState';
import { DownloadJobSlidesWindowState } from '../components/jobs/downloadJobSlidesWindowState';
import { SpeakerIdConfirmationWindowState } from '../components/speakerId/speakerIdConfirmationWindowState';
import { CancelAccountWindowState } from '../pages/account/cancelAccountWindowState';
import { DeceptioWindowState } from '../components/deceptioWindow/deceptioWindowState';

export function createWindowStores(store: Store) {
  return {
    editorWindow: new EditorWindowState(store),
    bookmarkWindow: new BookmarkWindowState(store),
    bookmarkListWindow: new BookmarkListWindowState(store),
    deleteBookmarkListWindow: new DeleteBookmarkListWindowState(store),
    shareVideoWindow: new ShareVideoWindowState(store),
    shareMeetingAssetsWindow: new ShareMeetingAssetsWindowState(store),
    multipleClipEditWindow: new MultipleClipEditWindowState(store),
    teamMembersWindow: new TeamMembersWindowState(store),
    teamDictionaryWindow: new TeamDictionaryWindowState(store),
    copyJobWindow: new CopyJobWindowState(store),
    addJobToTeamWindow: new AddJobToTeamWindowState(store),
    removeJobFromTeamWindow: new RemoveJobFromTeamWindowState(store),
    deleteJobWindow: new DeleteJobWindowState(store),
    retryJobWindow: new RetryJobWindowState(store),
    downloadJobWindow: new DownloadJobWindowState(store),
    exportToEdlWindow: new ExportToEdlWindowState(store),
    deleteStreamWindow: new DeleteStreamWindowState(store),
    removeJobFromCliprWindow: new RemoveJobFromCliprWindowState(store),
    ingestFromExternalLibraryWindow: new IngestFromExternalLibraryWindowState(store),
    renameJobWindow: new RenameJobWindowState(store),
    changeJobOwnerWindow: new ChangeJobOwnerWindowState(store),
    playerTutorialHelpButtonTooltip: new PlayerTutorialHelpButtonTooltipState(store),
    playerTutorialWindow: new PlayerTutorialWindowState(store, {}),
    postRegistrationWindow: new PostRegistrationTutorialState(store),
    playerTutorialTopicsWindow: new PlayerTutorialTopicsWindowState(store),
    playerTutorialConfirmationWindowState: new PlayerTutorialConfirmationWindowState(store),
    speakerWindow: new SpeakerWindowState(store),
    trackWindow: new TrackWindowState(store),
    deleteTrack: new DeleteTrackState(store),
    removeAccessPopup: new RemoveAccessPopupState(store),
    leaveTeamWindow: new LeaveTeamWindowState(store),
    videoInformationWindow: new VideoInformationWindowState(store),
    externalLibraryUploadWindow: new ExternalLibraryUploadWindowState(store),
    makeTeamPublicWindow: new MakeTeamPublicWindowState(store),
    toggleCliprGptWindow: new ToggleCliprGptWindowState(store),
    togglePublicSafetyWindow: new TogglePublicSafetyWindowState(store),
    toggleDeceptioAiWindow: new ToggleDeceptioAiWindowState(store),
    cancelUploadWindow: new CancelUploadWindowState(store),
    deleteMomentsPopup: new DeleteMomentsPopupState(store),
    clipWindow: new ClipWindowState(store),
    bulkInvitesWindow: new BulkInvitesWindowState(store),
    closeWindowConfirmationModal: new CloseWindowConfirmationModalState(store),
    confirmationModal: new ConfirmationModalState(store),
    mergeTracksWindow: new MergeTracksWindowState(store),
    jobErrorsWindow: new JobErrorsWindowState(store),
    commentDeleteWindow: new CommentDeleteWindowState(store),
    jobViewStatusWindow: new JobViewStatusWindowState(store),
    credentialsExpiredWindow: new CredentialsExpiredWindowState(store),
    zoomDateSelectorWindow: new ZoomDateSelectorWindowState(store),
    uploadThumbnailWindow: new UploadThumbnailWindowState(store),
    videoDetailsWindow: new VideoDetailsWindowState(store),
    scheduleStreamWindow: new ScheduleStreamWindowState(store),
    assetsWindow: new StreamAssetsWindowState(store),
    liveFeedWindow: new LiveFeedWindowState(store),
    liveNotStreamingWindow: new LiveNotStreamingWindowState(store),
    manualIngestWindow: new ManualIngestWindowState(store),
    streamQueueSettingsWindow: new StreamQueueSettingsWindowState(store),
    trainerKeyboardShortcutsWindow: new TrainerKeyboardShortcutsWindowState(store),
    infoModal: new InfoModalState(store),
    downloadWindow: new DownloadWindowState(store),
    downloadJobSlidesWindow: new DownloadJobSlidesWindowState(store),
    teamProductWindow: new TeamProductWindowState(store),
    deleteTeamWindow: new DeleteTeamWindowState(store),
    speakerIdWindow: new SpeakerIdConfirmationWindowState(store),
    cancelAccountWindow: new CancelAccountWindowState(store),
    deceptioWindow: new DeceptioWindowState(store),
  };
}

export type WindowStores = ReturnType<typeof createWindowStores>;
